<script setup>
import { computed } from 'vue'

/**
 * Inline SVG icon, replacing the old Material Symbols web font.
 *
 * The icons are vendored as SVG in resources/js/icons/ (outline + filled
 * variants), so there is no 3.9 MB font download, no FOIT/font-loading guard,
 * and icons render instantly with the HTML. Size and colour transfer from the
 * surrounding `text-*` classes for free: every SVG ships `width/height="1em"`
 * and `fill="currentColor"`, and the base `.app-icon` size (24px) mirrors the
 * old Material Symbols default.
 *
 * Usage mirrors the old span - the `name` is the same Material Symbols token
 * (e.g. `two_wheeler`), so existing icon strings in JS constants / PHP enum
 * getIcon() keep working unchanged:
 *
 *   <AppIcon name="search" />
 *   <AppIcon :name="item.icon" class="text-lg text-zinc-400" />
 *   <AppIcon name="favorite" :filled="isSaved" />   (fills the FILL axis)
 */
const props = defineProps({
    name: {
        type: String,
        required: true,
    },
    /** Render the filled variant (was the Material `FILL` axis at 1). */
    filled: {
        type: Boolean,
        default: false,
    },
})

const outlineSet = import.meta.glob('../../icons/material-symbols/*.svg', {
    query: '?raw',
    import: 'default',
    eager: true,
})
const filledSet = import.meta.glob('../../icons/material-symbols-fill/*.svg', {
    query: '?raw',
    import: 'default',
    eager: true,
})

const svg = computed(() => {
    const outlineKey = `../../icons/material-symbols/${props.name}.svg`
    const filledKey = `../../icons/material-symbols-fill/${props.name}.svg`
    const resolved = props.filled ? (filledSet[filledKey] ?? outlineSet[outlineKey]) : outlineSet[outlineKey]

    if (!resolved && import.meta.env.DEV) {
        console.warn(`[AppIcon] unknown icon name: "${props.name}"`)
    }

    return resolved ?? ''
})
</script>

<template>
    <!-- eslint-disable-next-line vue/no-v-html -- SVG is a trusted vendored asset, never user input -->
    <span
        class="app-icon"
        v-html="svg"
        :data-icon="name"
        aria-hidden="true"
    />
</template>
